import * as Sentry from "@sentry/react";

import { Utils } from "../utils";
import { AuthFactory } from './auth-factory';
import { BaseAuth } from './base-auth';
import { EAIClient } from "../../services/EAIClient";

export default class Auth {
  private EAIClient: EAIClient;

  constructor() {
    this.EAIClient = new EAIClient();
  }

  private getProvider(isFusionAuth: boolean): BaseAuth {
    return isFusionAuth
      ? AuthFactory.createFusionAuth()
      : AuthFactory.createAuth0();
  }

  login = (username: string, password: string, isFusionAuth: boolean, onError: (err: any) => void) => {
    const provider = this.getProvider(isFusionAuth);
    provider.login(username, password, onError);
  };

  loginRedirect(isFusionAuth: boolean = false) {
    const provider = this.getProvider(isFusionAuth);
    provider.loginRedirect();
  }

  postLoginRedirect(isFusionAuth: boolean) {
    const provider = this.getProvider(isFusionAuth);
    provider.postLoginRedirect();
  }

  authorizeSSOConnection = (connection: string, isFusionAuth: boolean) => {
    const provider = this.getProvider(isFusionAuth);
    provider.authorizeSSOConnection(connection);
  };

  resetPassword(email: string, isFusionAuth: boolean, onError: (err: any) => void, onSuccess: (suc: any) => void) {
    const provider = this.getProvider(isFusionAuth);
    provider.resetPassword(email, onError, onSuccess);
  }

  handleAuthentication = (isFusionAuth: boolean = false) => {
    const provider = this.getProvider(isFusionAuth);
    provider.handleAuthentication();
  };

  handleTokenRenew = async (isFusionAuth: boolean = false) => {
    const provider = this.getProvider(isFusionAuth);
    await provider.handleTokenRenew();
  };

  handleLogout = (callback: () => void, isFusionAuth: boolean = false) => {
    const provider = this.getProvider(isFusionAuth);
    provider.handleLogout(callback);
  };

  getAuthMethod = async (path: string) => {
    const resp = await this.EAIClient.getAuthMethod(path);
    return resp.data.data;
  };

  getUniversityUrl = async (email: string) => {
    const resp = await this.EAIClient.getUniversityUrl(email);
    return resp.data.data;
  };

  isAuthenticated = (isFusionAuth?: boolean) => {
    if (isFusionAuth === undefined) {
      // Keys to check in localstorage.
      const localStorageValidation = {
        current_user: "json",
        expires_at: "number",
        is_superadmin: "boolean",
        // school_list is a comma separated list, just check that it isn't null
        school_list: "",
        chosen_school_info: "json",
      };

      let areValuesGood = true;

      Object.keys(localStorageValidation).forEach(key => {
        const value = localStorage.getItem(key);
        if(value === null){
          areValuesGood = false;
        } else {
          switch(localStorageValidation[key]) {
            case "json":
              try{
                JSON.parse(value);
              } catch(e){
                Sentry.addBreadcrumb({
                  message: `Bad json value in localstorage key: ${key}`,
                  data: { value },
                });
                areValuesGood = false;
              }
              break;
            case "boolean":
              if(!["true", "false"].includes(value.toLowerCase())){
                Sentry.addBreadcrumb({
                  message: `Bad boolean value in localstorage for key ${key}`,
                  data: { value },
                });
                areValuesGood = false;
              }
              break;
            case "number":
              if(isNaN(parseInt(value))){
                Sentry.addBreadcrumb({
                  message: `Bad number value in localstorage for key ${key}`,
                  data: { value },
                });
                areValuesGood = false;
              }
              break;
            default:
              break;
          }
        }
      });

      if(!areValuesGood){
        // If a value in localStorage is bad clear them and
        //  have the user log in again to set them
        Utils.clearLocalstorage();
        return false;
      }

      // Otherwise, check the expiry listed in localstorage
      // The EAIClient will still log them out if their cookies have expired
      const expiresAtVal = localStorage.getItem("expires_at");
      if (!expiresAtVal) {
        return false;
      }
      const expiresAt = parseInt(expiresAtVal);

      return expiresAt ? new Date(expiresAt * 1000) > new Date() : false;
    }

    const provider = this.getProvider(isFusionAuth);
    return provider.isAuthenticated();
  };
}
